import type React from 'react';
import userPersonas from '@common/constants/userPersonas';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { Button, FieldSet, FormHelp, Label, LabeledControl, Legend, Radio, Text } from '@zillow/constellation-10';

type Props = {
  onSubmit: () => void;
  selectedOption: string;
  setSelectedOption: (selectedOption: string) => void;
  onClose: () => void;
  onBack: () => void;
  emergencyMode: boolean;
};

const BusinessTypeSelectorPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header="Payment setup"
      body={
        <FieldSet
          required
          legend={<Legend css={{ textStyle: 'body-lg-bold' }}>How is the business set up?</Legend>}
          description={<FormHelp>Choose the specific type of business entity</FormHelp>}
        >
          <LabeledControl
            controlId="business-type-single-member-llc"
            control={
              <Radio
                name="business-type"
                onChange={() => props.setSelectedOption('single_member_llc')}
                checked={props.selectedOption === 'single_member_llc'}
                value="single_member_llc"
              />
            }
            label={
              <Label>
                Single-member LLC
                <br />
                <Text css={{ color: 'text.subtle' }}>
                  You registered your business with the state, you are the single member, and it’s a separate entity for
                  tax purposes.
                </Text>
              </Label>
            }
          />
          <LabeledControl
            controlId="business-type-multi-member-llc"
            control={
              <Radio
                name="business-type"
                onChange={() => props.setSelectedOption('multi_member_llc')}
                checked={props.selectedOption === 'multi_member_llc'}
                value="multi_member_llc"
              />
            }
            label={
              <Label>
                Multi-member LLC or corporation
                <br />
                <Text css={{ color: 'text.subtle' }}>
                  Your business is registered with the state as an LLC or corporation with multiple members.
                </Text>
              </Label>
            }
          />
        </FieldSet>
      }
      backAction={{ onClick: props.onBack }}
      footerAriaLabel="Business type selection actions"
      footerEnd={
        <Button disabled={!props.selectedOption} onClick={props.onSubmit} emphasis="filled">
          Continue
        </Button>
      }
    />
  );
};

export default BusinessTypeSelectorPage;
